/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback } from 'react';

import PropTypes from 'prop-types';

import { Divider, MenuList } from '@coursera/cds-core';
import { SignOutIcon } from '@coursera/cds-icons';

import type { ContextProps } from 'bundles/enterprise-admin-home/components/ContextButton';
import { MenuItem } from 'bundles/enterprise-admin-navigation/components/side-nav/OrgSwitcher/items/MenuItem';
import { OrgContextItem } from 'bundles/enterprise-admin-navigation/components/side-nav/OrgSwitcher/items/OrgContextItem';
import { useAdminOrgSwitcherItems } from 'bundles/enterprise-admin-navigation/components/side-nav/config/useAdminOrgSwitcherItems';
import type { ResolvedOrgSwitcherItem } from 'bundles/enterprise-admin-navigation/components/side-nav/config/useAdminOrgSwitcherItems';
import { logout } from 'bundles/enterprise-admin-navigation/utils/AdminNavigationUtils';
import type { ThirdPartyOrganization as AdminDashboardOrg } from 'bundles/enterprise-admin/types';

import _t from 'i18n!nls/enterprise-admin-navigation';

type Props = {
  thirdPartyOrganization: AdminDashboardOrg;
  contexts: ContextProps[];
  /** Fires after any row is activated. The popup uses this to dismiss itself. */
  onItemActivate?: () => void;
};

type Context = {
  enableNewLearnersPage: boolean;
};

const styles = {
  divider: css`
    margin: var(--cds-spacing-50) 0;
  `,
};

/**
 * Body of the org switcher popup using CDS MenuList component.
 * Renders, in order: contexts → divider → settings + help → divider → logout.
 *
 * The link items (settings/help) come from the declarative `useAdminOrgSwitcherItems` config.
 * Logout is rendered directly here because it's an action (not a navigation),
 * keeping the config purely about links.
 *
 * Dynamic org-context rows come from `useOrgSwitcherData` (GraphQL-backed) and
 * are passed in by the parent.
 */
export const AdminOrgSwitcherContent = (
  { thirdPartyOrganization, contexts, onItemActivate }: Props,
  context: Context
) => {
  const { settings, help } = useAdminOrgSwitcherItems({ thirdPartyOrganization, enterpriseAdminFeatureFlags: context });

  const handleLogout = useCallback<() => void>(() => {
    logout();
    onItemActivate?.();
  }, [onItemActivate]);

  const renderLinkItem = (item: ResolvedOrgSwitcherItem) => (
    <MenuItem
      key={item.id}
      href={item.href}
      icon={item.icon}
      data-testid={`org-switcher-${item.id.toLowerCase()}`}
      onClick={onItemActivate}
      {...(item.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {item.label}
    </MenuItem>
  );

  const contextItems = contexts.map(({ key, href, header, label, photoUrl, primaryColor, iconColor }: ContextProps) => (
    <OrgContextItem
      key={key}
      href={href}
      header={header}
      label={label}
      photoUrl={photoUrl}
      primaryColor={primaryColor}
      iconColor={iconColor}
      onClick={onItemActivate}
    />
  ));

  const linkItems = [...settings, ...help];

  return (
    <MenuList aria-label={_t('Admin organization switcher')}>
      {contextItems}
      {linkItems.length > 0 && <Divider css={styles.divider} color="light" />}
      {linkItems.map(renderLinkItem)}
      <Divider css={styles.divider} color="light" />
      <MenuItem icon={SignOutIcon} onClick={handleLogout} data-testid="org-switcher-logout">
        {_t('Log Out')}
      </MenuItem>
    </MenuList>
  );
};

AdminOrgSwitcherContent.contextTypes = {
  enableNewLearnersPage: PropTypes.bool,
};

export default AdminOrgSwitcherContent;
